import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Banner from '../../ui/Banner';
import Toast from '../../ui/Toast';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import AiAssistantView from '../assistant/AiAssistantView';
import analyticsApi, { getAnalyticsErrorMessage } from './api/analyticsApi';
import OverviewTab from './tabs/OverviewTab';
import FunnelTab from './tabs/FunnelTab';
import CampaignsTab from './tabs/CampaignsTab';
import AttributionTab from './tabs/AttributionTab';
import CostTab from './tabs/CostTab';
import SeoLandingTab from './tabs/SeoLandingTab';
import ExportDialog from './components/ExportDialog';
import { CurrencySelect, Unavailable } from './components/ChartKit';
import './AnalyticsModule.css';

export const TABS = [
  { key: 'overview', label: 'Overview', icon: 'fa-gauge-high', exportable: true },
  { key: 'funnel', label: 'Funnel', icon: 'fa-filter', exportable: true },
  { key: 'campaigns', label: 'Campaigns', icon: 'fa-bullhorn', exportable: true },
  { key: 'attribution', label: 'Attribution', icon: 'fa-diagram-project', exportable: true },
  { key: 'cost', label: 'Cost & Usage', icon: 'fa-coins', exportable: true },
  { key: 'seo_landing', label: 'SEO & Landing Page', icon: 'fa-globe', exportable: true },
  { key: 'assistant', label: 'AI Assistant', icon: 'fa-robot' },
];

const PERIOD_LABELS = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  90: 'Last 90 days',
  180: 'Last 6 months',
  365: 'Last 12 months',
};

const TAB_COMPONENTS = {
  overview: OverviewTab,
  funnel: FunnelTab,
  campaigns: CampaignsTab,
  attribution: AttributionTab,
  cost: CostTab,
  seo_landing: SeoLandingTab,
};

const AnalyticsModule = () => {
  const { tab: tabParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useWorkspace();

  const normalizedTabParam = tabParam === 'seo-landing' ? 'seo_landing' : tabParam;
  const isAssistantRoute = location.pathname.startsWith('/ai-assistant');
  const activeTab = isAssistantRoute
    ? 'assistant'
    : TABS.some((t) => t.key === normalizedTabParam)
      ? normalizedTabParam
      : 'overview';
  const tabMeta = TABS.find((t) => t.key === activeTab);

  // null = let the server apply its configured default period.
  const [period, setPeriod] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  // Display currency for Overview money KPIs (totals are always computed in USD).
  const [currency, setCurrency] = useState('USD');
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const data = await analyticsApi.getDashboard(workspaceId, period);
      if (reqId === requestRef.current) setDashboard(data);
    } catch (err) {
      if (reqId === requestRef.current) {
        setError(await getAnalyticsErrorMessage(err, 'We could not load your analytics.'));
      }
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [workspaceId, period]);

  // Load once the user is on a dashboard tab; keep data while switching tabs.
  const needsData = activeTab !== 'assistant';
  useEffect(() => {
    if (needsData) load();
  }, [needsData, load]);

  const goToTab = (key) => navigate(key === 'assistant' ? '/ai-assistant' : `/analytics/${key}`);

  const handleTabKeyDown = (e, index) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length;
    goToTab(TABS[next].key);
    document.getElementById(`analytics-tab-${TABS[next].key}`)?.focus();
  };

  const section = dashboard?.sections?.[activeTab];
  const TabComponent = TAB_COMPONENTS[activeTab];
  const showExport =
    tabMeta?.exportable && dashboard?.canExport && section && section.available !== false;

  return (
    <div className={`analytics-page ${activeTab === 'assistant' ? 'is-assistant' : ''}`}>
      <Banner
        type="page-header"
        title="Analytics and Attribution"
        subtitle="See how your pipeline, meetings, campaigns and credits are performing, or ask the AI Assistant."
      />

      <div className="analytics-toolbar">
        <div className="analytics-tabs" role="tablist" aria-label="Analytics sections">
          {TABS.map((t, i) => (
            <button
              key={t.key}
              id={`analytics-tab-${t.key}`}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              aria-controls="analytics-panel"
              tabIndex={activeTab === t.key ? 0 : -1}
              className={`analytics-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => goToTab(t.key)}
              onKeyDown={(e) => handleTabKeyDown(e, i)}
            >
              <i className={`fas ${t.icon}`} aria-hidden="true"></i>
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {activeTab !== 'assistant' && (
          <div className="analytics-controls">
            <div className="analytics-controls-left">
              <label htmlFor="analytics-period" className="analytics-sr-only">
                Time period
              </label>
              <div className="analytics-select-wrapper">
                <i className="fa-regular fa-calendar select-icon-prefix" aria-hidden="true"></i>
                <select
                  id="analytics-period"
                  className="analytics-select"
                  value={period ?? dashboard?.period?.days ?? ''}
                  onChange={(e) => setPeriod(Number(e.target.value))}
                  disabled={!dashboard}
                >
                  {(dashboard?.period?.options || []).map((d) => (
                    <option key={d} value={d}>
                      {PERIOD_LABELS[d] || `Last ${d} days`}
                    </option>
                  ))}
                </select>
                <i className="fa-solid fa-chevron-down select-icon-suffix" aria-hidden="true"></i>
              </div>
              <button
                type="button"
                className="analytics-btn analytics-refresh-btn"
                onClick={load}
                disabled={loading}
                aria-label="Refresh analytics"
                title="Refresh"
              >
                <i
                  className={`fas fa-rotate-right ${loading ? 'fa-spin' : ''}`}
                  aria-hidden="true"
                ></i>
              </button>
              {activeTab === 'overview' && section?.available !== false && section?.fx && (
                <CurrencySelect value={currency} onChange={setCurrency} rates={section.fx.rates} />
              )}
            </div>
            {showExport && (
              <button
                type="button"
                className="analytics-btn analytics-export-btn"
                onClick={() => setExportOpen(true)}
              >
                <i className="fas fa-file-arrow-down" aria-hidden="true"></i>
                <span>Export CSV</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div
        id="analytics-panel"
        role="tabpanel"
        aria-labelledby={`analytics-tab-${activeTab}`}
        className="analytics-panel"
      >
        {activeTab === 'assistant' ? (
          <AiAssistantView embedded />
        ) : error ? (
          <div className="analytics-error" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
            <button type="button" className="analytics-btn" onClick={load}>
              Try again
            </button>
          </div>
        ) : !dashboard ? (
          <div className="analytics-loading" role="status">
            <i className="fas fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
            <span>Loading your analytics...</span>
          </div>
        ) : !section || section.available === false ? (
          <Unavailable reason={section?.reason} tab={activeTab} />
        ) : (
          <>
            {loading && (
              <div className="analytics-refreshing" role="status">
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Updating...
              </div>
            )}
            <TabComponent
              data={section}
              dashboard={dashboard}
              periodLabel={PERIOD_LABELS[dashboard.period.days]}
              currency={currency}
            />
          </>
        )}
      </div>

      {exportOpen && (
        <ExportDialog
          sectionLabel={tabMeta.label}
          minChars={dashboard?.limits?.exportPurposeMin}
          maxChars={dashboard?.limits?.exportPurposeMax}
          onCancel={() => setExportOpen(false)}
          onConfirm={async (purpose) => {
            try {
              const { blob, filename } = await analyticsApi.exportSection(workspaceId, {
                section: activeTab,
                period: period ?? dashboard?.period?.days,
                purpose,
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              setExportOpen(false);
              setToast({ message: 'Export downloaded.', type: 'success' });
            } catch (err) {
              return getAnalyticsErrorMessage(err, 'The export could not be created.');
            }
            return null;
          }}
        />
      )}

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'success' })}
          duration={4000}
        />
      )}
    </div>
  );
};

export default AnalyticsModule;
